import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, MapPin, Loader2, AlertTriangle } from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { getServerTime } from "../../../api/hrm/employees/attendanceLog";
import { showConfirmDialog } from "../../../components/ConfirmDialog";

const FOUR_HOURS_MIN = 4 * 60;

/** checkInTime: "HH:mm" hoặc "HH:mm:ss", checkoutTimeStr: từ server */
function getWorkDurationMinutes(checkInTime, workDateStr, checkoutTimeStr) {
  if (!checkInTime || !workDateStr || !checkoutTimeStr) return null;
  const parseParts = (t) => {
    const p = String(t).trim().split(":").map((n) => parseInt(n, 10));
    if (p.length < 2 || Number.isNaN(p[0])) return null;
    return { h: p[0], m: p[1] || 0, s: p[2] || 0 };
  };
  const ci = parseParts(checkInTime);
  const co = parseParts(checkoutTimeStr);
  if (!ci || !co) return null;

  const base = new Date(workDateStr);
  if (Number.isNaN(base.getTime())) return null;

  const start = new Date(base);
  start.setHours(ci.h, ci.m, ci.s, 0);
  const end = new Date(base);
  end.setHours(co.h, co.m, co.s, 0);
  if (end.getTime() <= start.getTime()) {
    end.setDate(end.getDate() + 1);
  }
  return (end - start) / 60000;
}

function formatDurationShort(totalMinutes) {
  if (totalMinutes == null || totalMinutes < 0) return "";
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  if (h > 0) return `${h} giờ ${m} phút`;
  return `${m} phút`;
}

const CheckoutConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  /** Giờ vào (từ phiếu chấm công), kết hợp workDate + giờ server để tính thời lượng */
  checkInTime,
  /** Ngày làm việc (YYYY-MM-DD hoặc ISO) */
  workDate,
}) => {
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [timeStr, setTimeStr] = useState("");
  const [error, setError] = useState("");

  /* =========================================================
     📌 Khi mở popup → Lấy giờ từ DB + Lấy GPS vị trí
  ========================================================== */
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError("");
      setTimeStr("");
      setLocation(null);

      // 🕒 Lấy giờ từ DB (BE → DB NOW())
      getServerTime()
        .then(({ time }) => {
          setTimeStr(time); // HH:mm:ss
        })
        .catch((err) => {
          console.error("❌ Lỗi lấy giờ server:", err);
          setError("Không lấy được giờ hệ thống từ server");
        });

      // 📍 Lấy vị trí thiết bị
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            setLocation({
              lat: latitude.toFixed(6),
              lon: longitude.toFixed(6),
            });
            setLoading(false);
          },
          (err) => {
            console.error("❌ Lỗi lấy định vị checkout:", err);
            setError("Không thể lấy vị trí. Vui lòng bật GPS hoặc cấp quyền lại.");
            setLoading(false);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      } else {
        setError("Trình duyệt không hỗ trợ định vị.");
        setLoading(false);
      }
    }
  }, [isOpen]);

  const workDurationMinutes = useMemo(
    () => getWorkDurationMinutes(checkInTime, workDate, timeStr),
    [checkInTime, workDate, timeStr]
  );

  const isUnderFourHours =
    workDurationMinutes != null && workDurationMinutes < FOUR_HOURS_MIN;

  const handleConfirmClick = async () => {
    if (isUnderFourHours) {
      const res = await showConfirmDialog({
        title: "Cảnh báo",
        text: "Thời gian làm việc chưa đủ 4 giờ, bạn có muốn tiếp tục checkout?",
        confirmText: "Tiếp tục checkout",
        cancelText: "Hủy",
      });
      if (!res.isConfirmed) return;
    }
    onConfirm({
      check_out_time: timeStr,
      check_out_location: location
        ? `${location.lat}, ${location.lon}`
        : "Không xác định",
    });
  };

  if (!isOpen) return null;

  /* =========================================================
     📌 JSX GIAO DIỆN — portal ra body để căn giữa viewport
  ========================================================== */
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-[9999] p-4"
      >
        <motion.div
          initial={{ scale: 0.9, y: 40 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-800">Xác nhận Checkout</h2>
            <button onClick={onClose} className="hover:bg-gray-100 rounded-full p-2">
              <X size={20} />
            </button>
          </div>

          {/* Nội dung */}
          <div className="p-6 space-y-4 text-gray-700">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin mb-2 text-gray-500" />
                <p>Đang lấy dữ liệu...</p>
              </div>
            ) : error ? (
              <p className="text-red-600">{error}</p>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span>
                    Thời gian checkout: <b>{timeStr}</b>
                  </span>
                </div>

                {checkInTime && workDurationMinutes != null && (
                  <p className="text-sm text-gray-600">
                    Thời gian làm việc (ước tính đến giờ checkout):{" "}
                    <b className="text-gray-800">{formatDurationShort(workDurationMinutes)}</b>
                  </p>
                )}

                {isUnderFourHours && (
                  <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                    <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                    <p>
                      Thời gian làm việc chưa đủ 4 giờ. Khi xác nhận checkout, bạn sẽ được hỏi lại.
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>
                    Tọa độ: <b>{location.lat}, {location.lon}</b>
                  </span>
                </div>

                {/* Map */}
                <div className="h-48 rounded-lg overflow-hidden border mt-3">
                  <MapContainer
                    center={[parseFloat(location.lat), parseFloat(location.lon)]}
                    zoom={16}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='© OpenStreetMap'
                    />
                    <Marker
                      position={[parseFloat(location.lat), parseFloat(location.lon)]}
                      icon={
                        new L.Icon({
                          iconUrl: "https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png",
                          iconSize: [32, 32],
                          iconAnchor: [16, 32],
                        })
                      }
                    />
                  </MapContainer>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="border border-gray-300 px-5 py-2 rounded hover:bg-gray-100 transition"
            >
              Hủy
            </button>

            <button
              onClick={handleConfirmClick}
              disabled={loading}
              className={`px-5 py-2 rounded font-medium text-white transition ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {loading ? "Đang xử lý..." : "Xác nhận Checkout"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CheckoutConfirmModal;
